import type {
  ScannerCacheStatus,
  ScannerPatientAttachment,
  ScannerPatientItem,
  ScannerPatientListResponse,
  SyncStatus,
} from "@/core/app/slices/integrations/integrations.type"

const DEFAULT_PAGE_SIZE = 25
const MAX_PAGE_SIZE = 100

function num(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value)
    if (Number.isFinite(n)) {
      return n
    }
  }
  return fallback
}

function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback
}

function cacheStatus(value: unknown): ScannerCacheStatus {
  if (value === "hit" || value === "empty" || value === "not_loaded") {
    return value
  }
  return "not_loaded"
}

function mapAttachment(raw: unknown): ScannerPatientAttachment {
  const r = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {}
  return {
    id: num(r.id),
    name: str(r.name),
    size: num(r.size),
  }
}

function mapPatient(raw: unknown): ScannerPatientItem {
  const r = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {}
  const attachments = Array.isArray(r.attachments_summary)
    ? r.attachments_summary.map(mapAttachment)
    : []

  return {
    external_case_id: num(r.external_case_id),
    patient_first_name: str(r.patient_first_name),
    patient_last_name: str(r.patient_last_name),
    patient_dob: str(r.patient_dob),
    doctor_name: str(r.doctor_name),
    practice_name: str(r.practice_name),
    status: str(r.status),
    attachment_count: num(r.attachment_count, attachments.length),
    attachments_summary: attachments,
    ...(typeof r.date_submitted === "string" ? { date_submitted: r.date_submitted } : {}),
    ...(typeof r.location === "string" ? { location: r.location } : {}),
    ...(typeof r.created_time === "string" ? { created_time: r.created_time } : {}),
  }
}

/** When the API omits `sync_status` (sparse / not_loaded bodies), treat list as settled for loaders. */
function deriveSyncStatus(syncRaw: string): SyncStatus {
  return (syncRaw === "" ? "completed" : syncRaw) as SyncStatus
}

function timestamp(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value)
    if (Number.isFinite(n)) {
      return n
    }
  }
  return 0
}

/** Shared routes often wrap payloads in `{ status, result }`; list POST may also return a flat body. */
function unwrapPatientListBody(data: unknown): Record<string, unknown> {
  if (data == null || typeof data !== "object") {
    return {}
  }
  const root = data as Record<string, unknown>
  const inner = root.result
  if (
    inner &&
    typeof inner === "object" &&
    Array.isArray((inner as Record<string, unknown>).patients)
  ) {
    return inner as Record<string, unknown>
  }
  return root
}

/**
 * Maps POST …/scanner/patients JSON to a consistent FE shape (handles sparse bodies
 * e.g. cache not_loaded, in_progress, empty cache).
 */
export function normalizeScannerPatientListResponse(
  data: unknown,
): ScannerPatientListResponse {
  const r = unwrapPatientListBody(data)
  const patients = Array.isArray(r.patients) ? r.patients.map(mapPatient) : []
  const cache_status = cacheStatus(r.cache_status)
  const sync_status = deriveSyncStatus(str(r.sync_status))

  const page = Math.max(1, num(r.page, 1))
  let page_size = num(r.page_size, DEFAULT_PAGE_SIZE)
  if (page_size < 1) {
    page_size = DEFAULT_PAGE_SIZE
  }
  page_size = Math.min(page_size, MAX_PAGE_SIZE)

  const total_count = num(r.total_count)
  const filtered_count = num(r.filtered_count)
  const loaded_count = num(r.loaded_count, total_count)
  let total_pages = num(r.total_pages)
  if (total_pages <= 0 && page_size > 0) {
    total_pages = Math.max(0, Math.ceil(filtered_count / page_size))
  }

  const doctors = Array.isArray(r.doctors)
    ? r.doctors.filter((d): d is string => typeof d === "string")
    : []

  let location: string[] | null = null
  if (r.location != null && Array.isArray(r.location)) {
    location = r.location.filter((x): x is string => typeof x === "string")
  }

  return {
    patients,
    sync_status,
    sync_message: str(r.sync_message),
    latest_sync_timestamp: timestamp(r.latest_sync_timestamp),
    total_count,
    loaded_count,
    filtered_count,
    page,
    page_size,
    total_pages,
    doctors,
    cache_status,
    enrichment_errors: num(r.enrichment_errors),
    location,
  }
}

export const scannerPatientListDefaults = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  maxPageSize: MAX_PAGE_SIZE,
} as const
